import { useState, useEffect } from 'react';
import { useContent } from '../hooks/useContent';
import { useSite } from '../hooks/useSites';
import { useToast } from '../hooks/useToast';
import { getSchema } from '../api/schemas';
import { getContentId } from '../types';
import FormField from './FormField';

interface PageSettingsProps {
  siteId: string;
  fields: string[];
}

export default function PageSettings({ siteId, fields: fieldNames }: PageSettingsProps) {
  const { site } = useSite(siteId);
  const { items, loading, create, update } = useContent(siteId, 'site');
  const { addToast } = useToast();

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  const schema = site ? getSchema(site.templateId) : undefined;
  const allFields = schema?.contentTypes.site?.fields || {};
  const existingItem = items[0];

  useEffect(() => {
    if (existingItem) {
      const data = existingItem.data || {};
      const subset: Record<string, unknown> = {};
      for (const name of fieldNames) {
        subset[name] = data[name as keyof typeof data];
      }
      setFormData(subset);
      setOriginalData(subset);
    }
  }, [existingItem, fieldNames]);

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalData);

  const handleSave = async () => {
    if (!siteId) return;
    setSaving(true);
    try {
      const fullData = { ...(existingItem?.data || {}), ...formData };
      if (existingItem) {
        const contentId = getContentId(existingItem.contentKey);
        await update(contentId, fullData as Record<string, unknown>);
      } else {
        await create(fullData as Record<string, unknown>);
      }
      setOriginalData({ ...formData });
      addToast('success', 'Page settings saved');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !schema) return null;

  return (
    <div className={`page-settings${open ? ' page-settings--open' : ''}`}>
      <button
        type="button"
        className="page-settings-summary"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
        Page Settings
        <svg className="page-settings-chevron" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      <div className="page-settings-body-wrap">
        <div>
          <div className="page-settings-body">
            <div className="editor-form">
              {fieldNames.map((name) => {
                const fieldSchema = allFields[name];
                if (!fieldSchema) return null;
                return (
                  <FormField
                    key={name}
                    name={name}
                    schema={{ ...fieldSchema, hidden: false }}
                    value={formData[name]}
                    onChange={handleChange}
                    siteId={siteId}
                  />
                );
              })}
            </div>
            <div className="page-settings-actions">
              {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Page Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
